import {useCallback, useRef, useState} from "react";
import {MapDocument} from "../../models/map.model";

// Feature-local undo for the Map Editor. Maps are NOT branch data — they must
// never dirty the branch or ride executeCommand's history — so the editor
// keeps its own bounded snapshot stack of just the paintable content
// (tileGrid / resources / spawns). A 64×64 grid is 4096 numbers; whole-content
// snapshots are cheap enough that inverse edits aren't worth their complexity.

const MAX_UNDO_DEPTH = 100;

interface MapSnapshot {
    // size travels with the grid — resizes are undoable, and a grid restored
    // without its matching size would be corrupt (length ≠ size²). The
    // pinned-variant images travel with the palette for the same reason.
    size: number;
    tileGrid: number[];
    tilePalette: string[];
    tilePaletteImages?: (string | null)[];
    resources: MapDocument['resources'];
    spawns: MapDocument['spawns'];
}

function takeSnapshot(doc: MapDocument): MapSnapshot {
    return {
        size: doc.size,
        tileGrid: [...doc.tileGrid],
        tilePalette: [...doc.tilePalette],
        tilePaletteImages: doc.tilePaletteImages ? [...doc.tilePaletteImages] : undefined,
        resources: doc.resources.map(r => ({...r})),
        spawns: doc.spawns.map(s => ({...s})),
    };
}

function applySnapshot(doc: MapDocument, snapshot: MapSnapshot): MapDocument {
    return {
        ...doc,
        size: snapshot.size,
        tileGrid: [...snapshot.tileGrid],
        tilePalette: [...snapshot.tilePalette],
        tilePaletteImages: snapshot.tilePaletteImages ? [...snapshot.tilePaletteImages] : undefined,
        resources: snapshot.resources.map(r => ({...r})),
        spawns: snapshot.spawns.map(s => ({...s})),
    };
}

export function useMapUndo() {
    const undoStack = useRef<MapSnapshot[]>([]);
    const redoStack = useRef<MapSnapshot[]>([]);
    // The stacks live in refs (event handlers mutate them mid-gesture), so a
    // version bump re-renders the owner whenever they change — that's what
    // keeps the toolbar's undo/redo buttons' enabled state truthful.
    const [, setVersion] = useState(0);
    const bump = useCallback(() => setVersion(v => v + 1), []);

    // Call with the PRE-edit document, once per committed stroke.
    const record = useCallback((doc: MapDocument) => {
        undoStack.current.push(takeSnapshot(doc));
        if (undoStack.current.length > MAX_UNDO_DEPTH) undoStack.current.shift();
        redoStack.current = [];
        bump();
    }, [bump]);

    const undo = useCallback((doc: MapDocument): MapDocument | undefined => {
        const snapshot = undoStack.current.pop();
        if (!snapshot) return undefined;
        redoStack.current.push(takeSnapshot(doc));
        bump();
        return applySnapshot(doc, snapshot);
    }, [bump]);

    const redo = useCallback((doc: MapDocument): MapDocument | undefined => {
        const snapshot = redoStack.current.pop();
        if (!snapshot) return undefined;
        undoStack.current.push(takeSnapshot(doc));
        bump();
        return applySnapshot(doc, snapshot);
    }, [bump]);

    // New/load: history from another document must not leak into this one.
    const reset = useCallback(() => {
        undoStack.current = [];
        redoStack.current = [];
        bump();
    }, [bump]);

    return {
        record, undo, redo, reset,
        // Computed at render time — accurate because every mutation bumps.
        canUndo: undoStack.current.length > 0,
        canRedo: redoStack.current.length > 0,
    };
}
